(() => {
  const selectors = {
    card: '[data-variant-card]',
    mainImage: '[data-card-main-image]',
    carousel: '[data-variant-carousel]',
    track: '[data-variant-track]',
    thumb: '[data-variant-thumb]',
    prev: '[data-variant-prev]',
    next: '[data-variant-next]',
  };

  const isHoverCapable = () =>
    window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  function storeOriginalImage(image) {
    if (image.dataset.variantOriginalStored === 'true') return;

    image.dataset.variantOriginalStored = 'true';
    image.dataset.variantOriginalSrc = image.getAttribute('src') || '';
    image.dataset.variantOriginalSrcset = image.getAttribute('srcset') || '';
    image.dataset.variantOriginalAlt = image.getAttribute('alt') || '';
  }

  function restoreOriginalImage(image) {
    image.setAttribute('src', image.dataset.variantOriginalSrc || '');
    image.setAttribute('srcset', image.dataset.variantOriginalSrcset || '');
    image.setAttribute('alt', image.dataset.variantOriginalAlt || '');
  }

  function applyThumbImage(image, thumb) {
    if (thumb.dataset.variantSrc) image.setAttribute('src', thumb.dataset.variantSrc);
    if (thumb.dataset.variantSrcset) image.setAttribute('srcset', thumb.dataset.variantSrcset);
    if (thumb.dataset.variantAlt) image.setAttribute('alt', thumb.dataset.variantAlt);
  }

  function initVariantSwap(card) {
    if (card.dataset.variantSwapInit === 'true') return;

    const mainImage = card.querySelector(selectors.mainImage);
    const thumbs = Array.from(card.querySelectorAll(selectors.thumb));
    if (!mainImage || !thumbs.length) return;

    card.dataset.variantSwapInit = 'true';
    storeOriginalImage(mainImage);

    let activeThumb = null;

    function setActive(thumb) {
      thumbs.forEach((item) => item.classList.remove('is-active'));
      activeThumb = thumb;
      if (activeThumb) activeThumb.classList.add('is-active');
    }

    thumbs.forEach((thumb) => {
      thumb.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          event.stopPropagation();

          if (activeThumb === thumb) {
            setActive(null);
            restoreOriginalImage(mainImage);
            return;
          }

          setActive(thumb);
          applyThumbImage(mainImage, thumb);
        },
        true
      );
    });

    if (!isHoverCapable()) return;

    thumbs.forEach((thumb) => {
      thumb.addEventListener('mouseenter', () => applyThumbImage(mainImage, thumb));
    });

    card.addEventListener('mouseleave', () => {
      if (activeThumb) {
        applyThumbImage(mainImage, activeThumb);
      } else {
        restoreOriginalImage(mainImage);
      }
    });
  }

  function initCarousel(carousel) {
    if (carousel.dataset.variantCarouselInit === 'true') return;

    const track = carousel.querySelector(selectors.track);
    const prev = carousel.querySelector(selectors.prev);
    const next = carousel.querySelector(selectors.next);
    if (!track || !prev || !next) return;

    carousel.dataset.variantCarouselInit = 'true';

    ['pointerdown', 'touchstart', 'mousedown', 'click'].forEach((eventName) => {
      carousel.addEventListener(eventName, (event) => event.stopPropagation());
    });

    function hasOverflow() {
      return track.scrollWidth > track.clientWidth + 2;
    }

    function updateNav() {
      const overflow = hasOverflow();
      [prev, next].forEach((button) => {
        button.style.display = overflow ? 'flex' : 'none';
        button.setAttribute('aria-hidden', overflow ? 'false' : 'true');
        button.setAttribute('tabindex', overflow ? '0' : '-1');
      });

      if (!overflow) {
        prev.disabled = true;
        next.disabled = true;
        return;
      }

      const maxScrollLeft = track.scrollWidth - track.clientWidth;
      prev.disabled = track.scrollLeft <= 1;
      next.disabled = track.scrollLeft >= maxScrollLeft - 1;
    }

    function scrollByStep(direction) {
      const step = Math.max(track.clientWidth * 0.85, 220);
      track.scrollBy({ left: direction * step, behavior: 'smooth' });
    }

    prev.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      scrollByStep(-1);
    });

    next.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      scrollByStep(1);
    });

    track.addEventListener(
      'wheel',
      (event) => {
        if (!hasOverflow() || event.shiftKey) return;
        event.preventDefault();
        track.scrollLeft += event.deltaY;
      },
      { passive: false }
    );

    let isDragging = false;
    let startX = 0;
    let startScrollLeft = 0;
    let moved = false;

    track.addEventListener('pointerdown', (event) => {
      isDragging = true;
      moved = false;
      startX = event.clientX;
      startScrollLeft = track.scrollLeft;
      track.setPointerCapture?.(event.pointerId);
    });

    track.addEventListener('pointermove', (event) => {
      if (!isDragging) return;
      const dx = event.clientX - startX;
      if (Math.abs(dx) > 6) moved = true;
      track.scrollLeft = startScrollLeft - dx;
    });

    ['pointerup', 'pointercancel'].forEach((eventName) => {
      track.addEventListener(eventName, () => {
        isDragging = false;
      });
    });

    track.addEventListener(
      'click',
      (event) => {
        if (!moved) return;
        event.preventDefault();
        event.stopPropagation();
        moved = false;
      },
      true
    );

    track.addEventListener('scroll', updateNav, { passive: true });
    updateNav();
    requestAnimationFrame(updateNav);
    window.addEventListener('resize', updateNav, { passive: true });
  }

  function init(root = document) {
    root.querySelectorAll(selectors.carousel).forEach(initCarousel);
    root.querySelectorAll(selectors.card).forEach(initVariantSwap);
  }

  function initAddedNode(node) {
    if (!(node instanceof Element)) return;

    if (node.matches(selectors.carousel)) initCarousel(node);
    if (node.matches(selectors.card)) initVariantSwap(node);
    init(node);
  }

  let dynamicContentObserver = null;

  function observeDynamicContent() {
    if (dynamicContentObserver || !window.MutationObserver || !document.body) return;

    dynamicContentObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach(initAddedNode);
      });
    });

    dynamicContentObserver.observe(document.body, { childList: true, subtree: true });
  }

  function start() {
    init();
    observeDynamicContent();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target || document));
})();
